import { Ga4Service } from '@core/services/ga4.service';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import {
  AnalyticsService,
  CheckRoomService,
  PriceService,
  ReqService,
  RoomBookingService,
} from '@core/services';
import { ExpertsTripWrapperService } from '@core/services/experts-trip-wrapper.service';
import { ProductType } from '@am/trips-update-service';
import { AmTripsAddProductComponent, Hotel, AmTripsAddProductModule } from '@am/trips-add-product';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.reducer';
import { selectSearchParams } from '@store/app-context/app-context.selectors';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { TrivagoService } from '@core/services/trivago.service';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { IRate } from '@core/models/Rate/Rate.interface';
import { RateStatus } from '../../../../../shared/enums/rate-status.enum';
import { Rate } from '@core/models/Rate/Rate.model';
import { AmExpertsCompensationComponent } from '@am/experts-compensation';
import { PriceComponent } from '../../../../../commons/price/price.component';
import { MbAdvice, MbButton } from '@am/moebius';
import { ProviderInfoComponent } from '../../../../../commons/provider-info/provider-info.component';
import { NgIf, AsyncPipe, DecimalPipe } from '@angular/common';

@Component({
    selector: 'total-price',
    templateUrl: './total-price.component.html',
    styleUrls: ['./total-price.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        AmTripsAddProductModule,
        ProviderInfoComponent,
        MbAdvice,
        PriceComponent,
        MbButton,
        AmExpertsCompensationComponent,
        AsyncPipe,
        DecimalPipe,
    ],
})
export class TotalPriceComponent implements OnInit {
  public readonly productType: ProductType = ProductType.HOTEL;

  @Input() set rateInput(rate: any) {
    this.rate = rate;
    this._rate$.next(rate);
  }

  @Input() set clusterInput(cluster: any) {
    this.cluster = cluster;
  }

  @Input() set experts(isExperts: any) {
    this.isExperts = isExperts;
  }

  @Input()
  showAmountTotal: any;

  @Input()
  priceByValue: any;

  @Input()
  hotelStaticInfo: any;

  @Input()
  hotelAvailabilityInfo: any;

  @Input()
  isMobile = false;

  @Input()
  isArgentinian: boolean;

  @ViewChild(AmTripsAddProductComponent)
  public amTripAddProduct: AmTripsAddProductComponent;

  public rate: any;
  public _rate$: Subject<Rate> = new BehaviorSubject(null);
  public cluster: any;
  public isExperts: any;
  public loadingRate: number;
  public isAlliance: boolean;
  public tripFavoriteData: Hotel;
  public currencyCodeToTripFavorite: string;
  public btnLabel = 'Reservar';

  constructor(
    private roomBookingService: RoomBookingService,
    private checkRoomRateService: CheckRoomService,
    private analyticsService: AnalyticsService,
    private priceService: PriceService,
    private ga4Service: Ga4Service,
    private expertsTripWrapperService: ExpertsTripWrapperService,
    private store: Store<AppState>,
    private contextService: ReqService,
    private trivagoService: TrivagoService,
  ) {}

  ngOnInit() {
    if (this.isExperts) {
      this.setTripFavoriteData();
    }
    this.setBtnLabel();
  }

  public setFinalDiscountPrice(rate) {
    return this.priceService.getPriceWithoutDiscount(
      rate,
      this.priceByValue,
      this.showAmountTotal,
    );
  }

  public showCommission(rate) {
    return rate.price.commission.value - rate.price.commission.tax;
  }

  public checkRate(rate, trackEvent = true) {
    if (this.roomBookingService.loading) {
      return false;
    } else {
      if (trackEvent) {
        const { recommended, country, city, amenities } = this.hotelAvailabilityInfo;
        this.ga4Service.add_to_cart(recommended, country, city, rate, amenities, !!this.cluster?.prioritized_by_rate_filter);
      }
      if (rate.isEconomic) {
        this.analyticsService.trackEvent({
          section: 'Detail',
          action: 'Economic Prepaid',
          label: 'Click',
        });
      }
      this.loadingRate = rate.id;
      if (rate.status === RateStatus.CHECKED_NO_CHANGE) {
        this.confirmReservation(rate);
      } else {
        this.checkRoomRateService
          .getRoomRate(rate, true)
          .pipe(take(1))
          .subscribe(
            (res) => {
              rate.clicked = true;
              switch (res.status) {
                case RateStatus.CHECKED_NO_CHANGE:
                  this.checkRate(res, false);
                  break;
                case RateStatus.CHECKED_CHANGE:
                  this.rate.changed = res.price.price_changed;
                  this.rate.price = res.price;
                  this._rate$.next({ ...this.rate });
                  break;
                case RateStatus.UNAVAILABLE:
                  rate.unavailable = this.cluster.rates.every((room) => {
                    return room.status === RateStatus.UNAVAILABLE;
                  });
                  break;
              }
              this.loadingRate = 0;
              rate.status = res.status;
            },
            (_err) => {
              rate.unavailable = this.cluster.rates.every((room) => {
                return room.status === RateStatus.UNAVAILABLE;
              });
              rate.status = RateStatus.UNAVAILABLE;
              this.loadingRate = 0;
            },
          );
      }
    }
  }

  public checkRateBeforeAddToTrip(event, rate: Rate) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.amTripAddProduct.loading.next(true);
    this.checkRoomRateService
      .getRoomRate(rate, false)
      .pipe(take(1))
      .subscribe((newRate: Rate) => {
        if (rate.status !== RateStatus.UNAVAILABLE) {
          this.tripFavoriteData.rate_id = newRate.id;
          this.amTripAddProduct.onClickAddToTrip();
        }
      });
  }

  public get rate$(): Observable<Rate> {
    return this._rate$.asObservable().pipe(filter(rate => rate !== null));
  }


  private confirmReservation(rate: IRate): void {
    this.roomBookingService
      .bookRate(rate, this.hotelAvailabilityInfo)
      .pipe(
        take(1),
        switchMap((res: any) =>
          forkJoin([
            this.expertsTripWrapperService.addToTrip(rate),
            this.trivagoService.sendTrivagoReference(rate.id),
          ]).pipe(map(() => res)) 
        )
      )
      .subscribe(
        (res) => {
          window.location.href =
            window.location.protocol +
            '//' +
            res.urlToRedirect;
          this.loadingRate = 0;
        },
        () => {
          (rate as any).error = true;
          this.loadingRate = 0;
        }
      );
  }

  private setBtnLabel() {
    if (this.isExperts || this.rate.price.currency.code.toUpperCase() === 'USD') {
      this.btnLabel = 'Seleccionar';
    }
  }

  private setTripFavoriteData() {
    this.store
      .select(selectSearchParams)
      .pipe(take(1))
      .subscribe((searchParams) => {
        const hotelStaticInfo = this.contextService.getHotelStaticInfo();

        this.tripFavoriteData = {
          id: hotelStaticInfo.id,
          checkin_time: hotelStaticInfo.checkin_time,
          checkout_time: hotelStaticInfo.checkout_time,
          room: searchParams.rooms,
          checkin: searchParams.checkIn,
          checkout: searchParams.checkOut,
        };

        if (this.rate) {
          this.tripFavoriteData.rate_id = this.rate.id;
          this.currencyCodeToTripFavorite = this.rate.price.currency.code;
        }
      });
  }
}
